/**
 * Rollback Tool
 * 
 * Tool for rolling back the working presentation to a previous checkpoint.
 * Used by ExecutionAgent when it determines a build result is incorrect.
 * 
 * The LLM can call this tool after inspecting a screenshot and deciding
 * that the built content is wrong and needs to be undone.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { backendClient } from "../../../../../services/communication/backendClient";
import {
    RollbackParamsSchema,
    type RollbackParams,
    type RollbackResponse,
} from "./rollback.schema";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const ROLLBACK_SYSTEM_PROMPT = `### rollback
Rollback the presentation to a previous checkpoint.

**When to use:**
- Screenshot shows something is WRONG (layout broken, content wrong)
- Need to undo a bad buildArea result
- Visual quality is poor and needs retry

**Only rollback if genuinely WRONG** - not for minor tweaks.

Each buildArea creates a 5-digit checkpoint ID (e.g., "12345").
After rollback, you can retry building the area differently.

\`\`\`json
{
  "checkpointId": "12345",
  "reason": "Title text overflows header area and overlaps body content"
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// ROLLBACK TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class RollbackTool extends ToolBase<RollbackParams, RollbackResponse> {
    public readonly name = "rollback";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = ROLLBACK_SYSTEM_PROMPT;
    
    /** Plan ID for this build session */
    private planId: string;
    
    constructor(planId: string) {
        super();
        this.planId = planId;
        
        this.description = new ToolDescription(
            "rollback",
            `Rollback the working presentation to a previous checkpoint.

Use this tool when you've built an area and the screenshot shows something is WRONG:
- Content doesn't match what was intended
- Layout is broken or overlapping
- Shapes are in the wrong position
- Visual quality is poor

Each buildArea call creates a 5-digit checkpoint ID (e.g., "12345").
You can rollback to any previous checkpoint to undo bad changes.

IMPORTANT:
- Only rollback if something is genuinely WRONG
- After rollback, you can try building the area again differently
- Include a clear reason for the rollback (helps with debugging)

Example:
{
  "checkpointId": "12345",
  "reason": "Title text overflows the header area and overlaps with body content"
}`,
            RollbackParamsSchema
        );
    }
    
    /**
     * Execute the rollback
     */
    public async execute(parameters: unknown): Promise<RollbackResponse> {
        const parseResult = safeParseToolParams(RollbackParamsSchema, parameters);
        
        if (!parseResult.success) {
            console.error('[RollbackTool] Invalid parameters:', parseResult.error);
            return {
                success: false,
                rolledBackTo: '',
                message: `Invalid parameters: ${parseResult.error}`,
                error: parseResult.error,
            };
        }
        
        const params = parseResult.data;
        
        console.log(`[RollbackTool] Rolling back to checkpoint: ${params.checkpointId}`);
        console.log(`[RollbackTool] Reason: ${params.reason}`);
        
        try {
            const response = await backendClient.rollbackBuild(
                this.planId,
                params.checkpointId
            );
            
            console.log(`[RollbackTool] Rollback successful: ${response.rolled_back_to}`);
            
            return {
                success: response.success,
                rolledBackTo: response.rolled_back_to,
                message: response.message,
            };
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[RollbackTool] Error: ${errorMessage}`);
            
            return {
                success: false,
                rolledBackTo: '',
                message: `Rollback failed: ${errorMessage}`,
                error: errorMessage,
            };
        }
    }
}

export default RollbackTool;

